import React, { useState } from "react";

import { Image, Schema } from "@at/design-system-elements";

import {
  dataAttrGUI,
  dataAttrTestId,
} from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";
import { VideoBuilder } from "@at/schema-utils/lib/builders/video";

import { expertImageSizes, getVideoImageLink } from "./expert-image-sizes";
import * as Styled from "./expert-review-video.styled";
import { useTracking } from "../../../../utils/tracking/use-tracking";
export interface VideoDetails {
  videoReference: string;
  title: string;
  description: string;
  uploadDate: string;
  duration: string;
}

interface VideoContainerProps {
  width?: number;
  height?: number;
  videoDetails?: VideoDetails;
  videoUrl?: string;
}

const buildYouTubeUrl = (videoId: string): string => {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
};

const VideoContainer = (props: VideoContainerProps) => {
  const { videoDetails, width = 560, height = 315, videoUrl } = props;
  return (
    <div
      {...{
        [dataAttrGUI]: "youtube-video-container",
        "data-testid": "expert-review-video",
      }}
    >
      <Styled.Iframe
        title="Video"
        width={width}
        height={height}
        src={
          videoDetails ? buildYouTubeUrl(videoDetails.videoReference) : videoUrl
        }
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

const buildVideoSchema = (videoDetails: VideoDetails) => {
  return VideoBuilder.aBuilder(videoDetails.title)
    .withDescription(videoDetails.description)
    .withUploadDate(videoDetails.uploadDate)
    .withDuration(videoDetails.duration)
    .withEmbedUrl(
      `https://www.youtube.com/embed/${videoDetails.videoReference}`,
    )
    .withThumbnailUrl(
      `https://i.ytimg.com/vi/${videoDetails.videoReference}/maxresdefault`,
    )
    .build();
};

export interface VideoProps {
  youtubeVideoUrl?: string;
  viewTrackingEventText?: string;
  videoDetails?: VideoDetails;
}

const extractVideoID = (url: string): string | undefined => {
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match ? match[7] : undefined;
};

export const ExpertReviewVideo = (props: VideoProps) => {
  const { viewTrackingEventText, youtubeVideoUrl, videoDetails } = props;
  let videoId = videoDetails?.videoReference ?? "";

  if (youtubeVideoUrl && !videoId) {
    videoId = extractVideoID(youtubeVideoUrl) ?? "";
  }

  const nonYoutubeVideo = videoId == null;
  const [videoVisible, setVideoVisible] = useState(nonYoutubeVideo);

  const { trackEvent } = useTracking();

  return (
    <Styled.VideoWrapper
      {...{ [dataAttrTestId]: "expert-review-video-wrapper" }}
    >
      {videoDetails && <Schema schema={buildVideoSchema(videoDetails)} />}
      {!videoVisible && (
        <>
          <Image
            src={getVideoImageLink(videoId, "s")}
            shouldLazyLoad={true}
            srcSet={expertImageSizes(videoId)}
            alt={"Review video placeholder"}
          />

          <Styled.PlayButton
            {...{ [dataAttrGUI]: "play-button" }}
            aria-label="Watch review video"
            data-testid="expert-review-video-play-button"
            onClick={() => {
              setVideoVisible(true);
              trackEvent({
                trackingLabel:
                  viewTrackingEventText ?? "leasing_expert_review_video_click",
              });
            }}
          />
        </>
      )}
      {videoVisible && (
        <VideoContainer
          videoDetails={videoDetails}
          videoUrl={
            !videoDetails && !nonYoutubeVideo && videoId
              ? buildYouTubeUrl(videoId)
              : youtubeVideoUrl
          }
          width={640}
          height={385}
        />
      )}
    </Styled.VideoWrapper>
  );
};
